import React, { useMemo } from "react";

import { Stack, Typography } from "@mui/material";

import DynamicFieldTable from "components/organisms/DynamicFieldTable";

const LinkedinSignalMappingContent = ({
    conversions,
    events,
    selectedMappings,
    setSelectedMappings,
}) => {
    // Transform selectedMappings to DynamicFieldTable format
    const transformedFields = useMemo(() => {
        if (!selectedMappings || selectedMappings.length === 0) {
            return [
                {
                    conversion_id: {
                        value: "",
                        options: conversions.map((conv) => ({
                            label: conv.name || conv.conversion_name || "",
                            value: conv.id?.toString(),
                        })),
                    },
                    event: {
                        value: "",
                        options: events.map((evt) => ({
                            label: evt.name,
                            value: evt.code,
                        })),
                    },
                    rowDisabledTooltip: "At least one mapping is required",
                },
            ];
        }

        return selectedMappings.map((mapping) => {
            // Get already selected conversion IDs
            const selectedConversionIds = (selectedMappings || []).map((m) =>
                m.conversion_id?.toString(),
            );

            // Filter conversions: show current or unselected ones
            const availableConversions = (conversions || [])
                .filter(
                    (conv) =>
                        conv.id?.toString() ===
                            mapping.conversion_id?.toString() ||
                        !selectedConversionIds.includes(conv.id?.toString()),
                )
                .map((conv) => ({
                    label: conv.name || conv.conversion_name || "",
                    value: conv.id?.toString(),
                }));

            // Transform events to options
            const eventOptions = (events || []).map((evt) => ({
                label: evt.name,
                value: evt.code,
            }));

            return {
                conversion_id: {
                    value: mapping.conversion_id || "",
                    options: availableConversions,
                },
                event: {
                    value: mapping.event || "",
                    options: eventOptions,
                },
            };
        });
    }, [selectedMappings, conversions, events]);
    // Transform fields back to selectedMappings format
    const handleFieldsChange = (newFields) => {
        const newMappings = newFields.map((field) => {
            const conversionId = field.conversion_id?.value || "";
            const conv = (conversions || []).find(
                (c) => c.id?.toString() === conversionId?.toString(),
            );

            return {
                conversion_id: conversionId,
                event: field.event?.value || "",
                conversion_name: conv?.name || conv?.conversion_name || "",
            };
        });

        setSelectedMappings(newMappings);
    };

    // Calculate if there are any available conversions left to map
    const hasAvailableConversions = useMemo(() => {
        const selectedConversionIds = (selectedMappings || [])
            .map((m) => m.conversion_id)
            .filter(Boolean);

        const availableCount = (conversions || []).filter(
            (conv) => !selectedConversionIds.includes(conv.id?.toString()),
        ).length;

        return availableCount > 0;
    }, [selectedMappings, conversions]);

    // Field configuration for DynamicFieldTable
    const fieldsConfiguration = [
        {
            name: "event",
            label: "Internal Event",
            variant: "FREESOLO_SELECT",
            placeholder: "Select or enter custom event",
        },
        {
            name: "conversion_id",
            label: "LinkedIn Conversion",
            variant: "SELECT",
            placeholder: "Select Conversion",
        },
    ];

    return (
        <Stack spacing={2}>
            <Typography variant="body2" fontWeight={400}>
                Map LinkedIn conversions to your internal events. You can add or
                remove mappings as needed. At least one mapping is required.
            </Typography>

            <DynamicFieldTable
                isScrollable={false}
                maxHeight={null}
                fields={transformedFields}
                setFields={handleFieldsChange}
                fieldsConfiguration={fieldsConfiguration}
                minLength={1}
                addMoreDisabled={
                    conversions?.length === selectedMappings?.length
                }
                addMoreTooltip="There are no available conversions"
                showStatus={false}
            />
        </Stack>
    );
};

export default LinkedinSignalMappingContent;
